/**
 * useBooleanSearch.js
 * 
 * Parses a boolean search string and filters candidates.
 * Supports: AND, OR, NOT, quoted phrases, parentheses
 * 
 * Examples:
 *   java OR springboot
 *   python AND (aws OR gcp) NOT intern
 *   "machine learning" AND python
 */

/**
 * Parse boolean query string into tokens
 * Returns { must: [], should: [], mustNot: [] }
 */
export function parseBooleanQuery(queryString) {
  if (!queryString || !queryString.trim()) {
    return { must: [], should: [], mustNot: [], raw: "" };
  }

  const raw = queryString.trim();

  // Check if it's a boolean query (contains AND/OR/NOT keywords)
  const isBooleanQuery = /\b(AND|OR|NOT)\b/i.test(raw);

  if (!isBooleanQuery) {
    // Plain search — treat as a single MUST term (existing behavior)
    return { must: [raw.toLowerCase()], should: [], mustNot: [], raw };
  }

  const must = [];
  const should = [];
  const mustNot = [];

  // Tokenize: split by AND/OR/NOT while keeping the operators
  // Handles: quoted phrases, parentheses groups
  const tokenRegex = /\b(AND|OR|NOT)\b|"([^"]+)"|(\([^)]+\))|([^\s()"]+)/gi;
  const tokens = [];
  let match;

  while ((match = tokenRegex.exec(raw)) !== null) {
    if (match[1]) tokens.push({ type: "op", value: match[1].toUpperCase() });
    else if (match[2]) tokens.push({ type: "term", value: match[2].toLowerCase() }); // quoted
    else if (match[3]) {
      // Parenthesized group — extract inner terms as OR group
      const inner = match[3].replace(/[()]/g, "").trim();
      tokens.push({ type: "group", value: inner.toLowerCase() });
    } else if (match[4]) tokens.push({ type: "term", value: match[4].toLowerCase() });
  }

  // Walk tokens and assign to must/should/mustNot
  let currentOp = "AND"; // default operator before first term

  tokens.forEach((token, i) => {
    if (token.type === "op") {
      currentOp = token.value;
      return;
    }

    if (token.type === "term") {
      if (currentOp === "NOT") mustNot.push(token.value);
      else if (currentOp === "OR") should.push(token.value);
      else must.push(token.value); // AND or default
    }

    if (token.type === "group") {
      // Parenthesized terms always go to should (OR group)
      const innerTerms = token.value.split(/\s+OR\s+|\s+/i).map(t => t.trim()).filter(Boolean);
      if (currentOp === "NOT") innerTerms.forEach(t => mustNot.push(t));
      else if (currentOp === "AND") innerTerms.forEach(t => must.push(t)); // AND (group) — all required
      else innerTerms.forEach(t => should.push(t));
    }

    // Reset op after consuming a term (next default is AND)
    currentOp = "AND";
  });

  return { must, should, mustNot, raw };
}

/**
 * Match a single candidate against parsed boolean query
 */
export function matchesQuery(candidate, parsedQuery) {
  const { must, should, mustNot } = parsedQuery;

  if (!must.length && !should.length && !mustNot.length) return true;

  // Build a searchable text blob from candidate fields
  const searchBlob = [
    candidate.name || "",
    (candidate.skills || []).join(" "),
    candidate.location || "",
    candidate.visaType || "",
    candidate.email || "",
    candidate.rawText || candidate.raw_text || "",
    String(candidate.experience || ""),
  ]
    .join(" ")
    .toLowerCase();

  // AND — every must term must appear
  if (must.length > 0) {
    const allMustMatch = must.every(term => searchBlob.includes(term));
    if (!allMustMatch) return false;
  }

  // OR — at least one should term must appear (skip check if empty)
  if (should.length > 0) {
    const anyShouldMatch = should.some(term => searchBlob.includes(term));
    if (!anyShouldMatch) return false;
  }

  // NOT — none of mustNot terms should appear
  if (mustNot.length > 0) {
    const anyMustNotMatch = mustNot.some(term => searchBlob.includes(term));
    if (anyMustNotMatch) return false;
  }

  return true;
}

/**
 * Filter candidates array with boolean search
 */
export function booleanFilter(candidates, queryString) {
  const parsed = parseBooleanQuery(queryString);
  return {
    results: candidates.filter(c => matchesQuery(c, parsed)),
    parsed,
  };
}

/**
 * Extract all search terms that should be highlighted
 * (must + should terms, not mustNot)
 */
export function getHighlightTerms(parsedQuery) {
  return [...(parsedQuery.must || []), ...(parsedQuery.should || [])];
}

/**
 * Detect if query string looks like boolean syntax
 */
export function isBooleanSyntax(query) {
  return /\b(AND|OR|NOT)\b/i.test(query);
}